import React from 'react';
import { AssetDossier, UserSavedScenario, StudioSession } from '../../types';
import { getIcMemoDownloadUrl } from '../../lib/api';
import { canExportCleanPdf } from '../../config/accessTiers';
import {
  X,
  Download,
  Printer,
  ShieldCheck,
  Building,
  MapPin,
  Calendar,
  Zap,
  DollarSign,
  TrendingUp,
  FileText,
  Lock,
} from 'lucide-react';

import { generateInstitutionalIcMemoPdf } from '../../lib/pdfMemoGenerator';

interface IcMemoModalProps {
  isOpen: boolean;
  onClose: () => void;
  dossier: AssetDossier | null;
  scenario?: UserSavedScenario | null;
  session: StudioSession;
}

export const IcMemoModal: React.FC<IcMemoModalProps> = ({
  isOpen,
  onClose,
  dossier,
  scenario,
  session,
}) => {
  if (!isOpen || !dossier) return null;

  const isCleanPdf = canExportCleanPdf(session.tier);
  const tx = dossier.transaction;
  const deeds = dossier.deeds || [];
  const leases = dossier.leases || [];
  const pilots = dossier.pilots || [];

  // Model financials from scenario or fallback
  const itMw = scenario ? scenario.it_load_mw : tx?.headline_capacity_mw || dossier.capacity_mw || 250;
  const rentKw = scenario ? scenario.rent_rate_usd_kw_month : leases[0]?.rent_rate_usd_kw_month || 185;
  const capexPerMw = scenario ? scenario.capex_per_mw_usd || 8_500_000 : 8_500_000;
  const totalCapex = scenario ? scenario.total_capex_usd : itMw * capexPerMw;
  const debtLtv = scenario ? scenario.debt_ltv_pct || 65 : 65;
  const debtAmt = (totalCapex * debtLtv) / 100;
  const equityAmt = totalCapex - debtAmt;
  const annualNoi = scenario ? scenario.annual_noi_usd : itMw * 1000 * rentKw * 12 * 0.95;
  const yieldOnCost = scenario ? scenario.unlevered_yield_on_cost_pct : (annualNoi / totalCapex) * 100;
  const leveredIrr = scenario ? scenario.levered_irr_5yr_pct : 26.4;

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    generateInstitutionalIcMemoPdf(dossier, scenario);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
      <div
        className="w-full max-w-4xl bg-[#0c121e] border border-[#1a253b] rounded-2xl shadow-2xl text-slate-100 overflow-hidden flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 bg-[#090e1a] border-b border-[#1a253b] flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-[#2979ff]/15 text-[#2979ff] border border-[#2979ff]/30 font-bold">
              INVESTMENT COMMITTEE DEAL MEMORANDUM
            </span>
            <span className="text-xs text-slate-400 font-mono">
              CONFIDENTIAL &bull; {dossier.canonical_project_id}
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleDownloadPdf}
              className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-[#2979ff] hover:bg-[#1c68e8] text-white text-xs font-semibold shadow-sm transition"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Download PDF</span>
              {!isCleanPdf && <span className="text-[10px] opacity-75">(Watermarked)</span>}
            </button>

            <button
              onClick={handlePrint}
              className="p-1.5 rounded-lg bg-[#121a2d] hover:bg-[#1a253b] border border-[#1a253b] text-slate-300 hover:text-white transition"
              title="Print Memo"
            >
              <Printer className="w-4 h-4" />
            </button>

            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white bg-[#121a2d] hover:bg-[#1a253b] border border-[#1a253b] transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Memo Body */}
        <div className="p-6 overflow-y-auto space-y-6 font-sans text-xs print:text-black print:bg-white">
          {/* Executive Header */}
          <div className="border-b border-[#1a253b] pb-4 flex justify-between items-start">
            <div>
              <div className="text-[11px] font-mono text-slate-400 uppercase tracking-wider">
                MEMORANDUM TO INVESTMENT COMMITTEE
              </div>
              <h1 className="text-xl font-bold text-white mt-1">
                {dossier.canonical_name} ({itMw} MW Hyperscale Compute Campus)
              </h1>
              <p className="text-slate-400 mt-0.5">
                Location: <b className="text-slate-200">{dossier.county} County, {dossier.state}</b> &bull; ISO/RTO: <b className="text-slate-200">{dossier.iso_rto || 'Regional Grid'}</b>
              </p>
            </div>

            <div className="text-right font-mono text-xs">
              <div className="text-slate-400">Date: {new Date().toLocaleDateString()}</div>
              <div className="text-[#2979ff] font-semibold mt-1">Recommendation: PROCEED TO LOI</div>
            </div>
          </div>

          {/* Underwriting Metrics Grid */}
          <div className="space-y-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#2979ff]">
              1. Key Financial &amp; Underwriting Metrics
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 font-mono">
              <div className="p-3 rounded-lg bg-[#06090e] border border-[#1a253b]">
                <span className="text-[10px] text-slate-500 block">Total Capex</span>
                <span className="text-base font-bold text-white">${(totalCapex / 1_000_000).toFixed(1)}M</span>
                <span className="text-[10px] text-slate-500 block">Equity: ${(equityAmt / 1_000_000).toFixed(1)}M</span>
              </div>
              <div className="p-3 rounded-lg bg-[#06090e] border border-[#1a253b]">
                <span className="text-[10px] text-slate-500 block">Net Operating Income</span>
                <span className="text-base font-bold text-emerald-400">${(annualNoi / 1_000_000).toFixed(1)}M/yr</span>
                <span className="text-[10px] text-slate-500 block">Rate: ${rentKw}/kW-mo</span>
              </div>
              <div className="p-3 rounded-lg bg-[#06090e] border border-[#1a253b]">
                <span className="text-[10px] text-slate-500 block">Yield on Cost</span>
                <span className="text-base font-bold text-amber-400">{yieldOnCost.toFixed(2)}%</span>
                <span className="text-[10px] text-slate-500 block">Unlevered Return</span>
              </div>
              <div className="p-3 rounded-lg bg-[#06090e] border border-[#1a253b]">
                <span className="text-[10px] text-slate-500 block">5-Yr Levered IRR</span>
                <span className="text-base font-bold text-cyan-400">{leveredIrr.toFixed(1)}%</span>
                <span className="text-[10px] text-slate-500 block">{debtLtv}% Debt LTV</span>
              </div>
            </div>
          </div>

          {/* Property Conveyance & Deeds */}
          <div className="space-y-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-amber-400">
              2. Real Property Conveyance &amp; Recorded Deed Chain
            </h3>
            {deeds.length > 0 ? (
              <div className="space-y-2 font-mono">
                {deeds.map((d) => (
                  <div key={d.deed_id} className="p-3 rounded-lg bg-[#06090e] border border-[#1a253b] text-xs">
                    <div className="flex justify-between font-bold text-white">
                      <span>{d.document_type || 'SPECIAL WARRANTY DEED'}</span>
                      <span className="text-amber-400">Recorded: {d.recording_date}</span>
                    </div>
                    <div className="text-slate-400 mt-1">
                      County: {d.county_name}, {d.state_code} &bull; Instrument: <b className="text-slate-200">{d.instrument_number || 'N/A'}</b> &bull; Acreage: {d.conveyed_acreage} ac
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Grantor: {d.grantor_raw || 'SPV'} &rarr; Grantee: {d.grantee_raw || 'Hyperscaler'}
                    </div>
                    <div className="text-[10px] text-slate-600 truncate mt-1">
                      SHA-256: {d.evidence_sha256}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 italic">No public deed recordings on file.</p>
            )}
          </div>

          {/* Wholesale Leases & Tenants */}
          <div className="space-y-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-cyan-400">
              3. Wholesale Colocation Leases &amp; SEC EDGAR Disclosures
            </h3>
            {leases.length > 0 ? (
              <div className="space-y-2 font-mono">
                {leases.map((l) => (
                  <div key={l.lease_id} className="p-3 rounded-lg bg-[#06090e] border border-[#1a253b] text-xs">
                    <div className="flex justify-between font-bold text-white">
                      <span>Tenant: {l.tenant_organization_id.replace('AOI-', '')}</span>
                      <span className="text-cyan-400">${l.rent_rate_usd_kw_month}/kW-mo</span>
                    </div>
                    <div className="text-slate-400 mt-1">
                      Contracted IT: {l.contracted_it_mw} MW &bull; Term: {l.lease_term_years} Yrs &bull; Escalator: {l.annual_escalator_pct}%/yr
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 italic">Hyperscale single-tenant structure; terms benchmarked to regional colocation comps.</p>
            )}
          </div>

          {/* Statutory Tax Abatements */}
          <div className="space-y-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-400">
              4. Municipal PILOT Agreements &amp; Statutory Tax Abatement
            </h3>
            {pilots.length > 0 ? (
              <div className="space-y-2 font-mono">
                {pilots.map((p) => (
                  <div key={p.incentive_id} className="p-3 rounded-lg bg-[#06090e] border border-[#1a253b] text-xs">
                    <div className="flex justify-between font-bold text-white">
                      <span>{p.jurisdiction_name}</span>
                      <span className="text-emerald-400">{p.real_property_abatement_pct}% RE Abatement</span>
                    </div>
                    <div className="text-slate-400 mt-1">
                      Term: {p.term_years} Years &bull; Servers Exempt: {p.personal_property_server_exempt ? 'YES' : 'NO'} &bull; Sales Tax: {p.sales_and_use_tax_exempt ? 'EXEMPT' : 'TAXABLE'}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Statutory Authority: {p.statutory_authority || 'County Economic Development Ordinance'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 italic">Standard statutory assessment applies.</p>
            )}
          </div>

          {/* Universal Evidence Provenance */}
          <div className="p-3.5 rounded-lg bg-[#06090e] border border-[#1a253b] flex items-center justify-between text-[11px] font-mono text-slate-400">
            <div className="flex items-center space-x-2">
              <ShieldCheck className="w-4 h-4 text-[#2979ff]" />
              <span>Universal Evidence Provenance Verified (AIxEnergy Studio IC Registry)</span>
            </div>
            <span className="text-slate-500">SHA-256 Audit Trail Attached</span>
          </div>
        </div>
      </div>
    </div>
  );
};
